import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export class TimeUtil {
  private static serverTimezone: string = 'Asia/Shanghai';

  /**
   * 设置服务器时区
   */
  static setServerTimezone(timezone: string) {
    this.serverTimezone = timezone;
  }

  /**
   * 获取服务器时区
   */
  static getServerTimezone(): string {
    return this.serverTimezone;
  }

  /**
   * 格式化服务器时间（统一时区显示）
   */
  static formatServerTime(dateStr: string | Date, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    return dayjs(dateStr).tz(this.serverTimezone).format(format);
  }

  /**
   * 格式化本地时间
   */
  static formatLocalTime(dateStr: string | Date, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    return dayjs(dateStr).format(format);
  }

  /**
   * 获取当前本地时间
   */
  static now(): dayjs.Dayjs {
    return dayjs();
  }

  /**
   * 获取当前服务器时区时间
   */
  static nowServer(): dayjs.Dayjs {
    return dayjs().tz(this.serverTimezone);
  }

  /**
   * 将服务器时间转换为本地时间显示
   */
  static serverToLocal(dateStr: string | Date): dayjs.Dayjs {
    return dayjs(dateStr).tz(this.serverTimezone);
  }

  /**
   * 解析日期字符串
   */
  static parse(dateStr: string): dayjs.Dayjs {
    return dayjs(dateStr);
  }

  /**
   * 解析服务器时区日期字符串
   */
  static parseServerTime(dateStr: string): dayjs.Dayjs {
    return dayjs(dateStr).tz(this.serverTimezone);
  }

  /**
   * 格式化相对时间（如：2小时前）
   */
  static formatRelativeTime(dateStr: string | Date): string {
    return dayjs(dateStr).fromNow();
  }

  /**
   * 格式化服务器时区相对时间
   */
  static formatServerRelativeTime(dateStr: string | Date): string {
    return dayjs(dateStr).tz(this.serverTimezone).fromNow();
  }

  /**
   * 检查是否为今天
   */
  static isToday(dateStr: string | Date): boolean {
    return dayjs(dateStr).isSame(dayjs(), 'day');
  }

  /**
   * 检查服务器时区是否为今天
   */
  static isServerToday(dateStr: string | Date): boolean {
    return dayjs(dateStr).tz(this.serverTimezone).isSame(dayjs().tz(this.serverTimezone), 'day');
  }

  /**
   * 获取时间戳（毫秒）
   */
  static timestamp(): number {
    return Date.now();
  }

  /**
   * 格式化时间戳
   */
  static formatTimestamp(timestamp: number, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    return dayjs(timestamp).format(format);
  }

  /**
   * 格式化服务器时区时间戳
   */
  static formatServerTimestamp(timestamp: number, format: string = 'YYYY-MM-DD HH:mm:ss'): string {
    return dayjs(timestamp).tz(this.serverTimezone).format(format);
  }

  /**
   * 计算两个时间之间的差值（毫秒）
   */
  static diff(startTime: number | string | Date, endTime?: number | string | Date): number {
    const end = endTime || this.timestamp();
    return dayjs(end).diff(dayjs(startTime));
  }

  /**
   * 格式化持续时间（毫秒）为可读格式
   */
  static formatDuration(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) {
      return `${days}天${hours % 24}小时${minutes % 60}分钟`;
    } else if (hours > 0) {
      return `${hours}小时${minutes % 60}分钟${seconds % 60}秒`;
    } else if (minutes > 0) {
      return `${minutes}分钟${seconds % 60}秒`;
    } else {
      return `${seconds}秒`;
    }
  }

  /**
   * 获取日期范围（今天、昨天、本周、本月等）
   */
  static getDateRange(type: 'today' | 'yesterday' | 'week' | 'month' | 'year'): [dayjs.Dayjs, dayjs.Dayjs] {
    const now = dayjs();
    
    switch (type) {
      case 'today':
        return [now.startOf('day'), now.endOf('day')];
      case 'yesterday':
        const yesterday = now.subtract(1, 'day');
        return [yesterday.startOf('day'), yesterday.endOf('day')];
      case 'week':
        return [now.startOf('week'), now.endOf('week')];
      case 'month':
        return [now.startOf('month'), now.endOf('month')];
      case 'year':
        return [now.startOf('year'), now.endOf('year')];
      default:
        return [now.startOf('day'), now.endOf('day')];
    }
  }

  /**
   * 获取服务器时区日期范围
   */
  static getServerDateRange(type: 'today' | 'yesterday' | 'week' | 'month' | 'year'): [dayjs.Dayjs, dayjs.Dayjs] {
    const now = dayjs().tz(this.serverTimezone);
    
    switch (type) {
      case 'today':
        return [now.startOf('day'), now.endOf('day')];
      case 'yesterday':
        const yesterday = now.subtract(1, 'day');
        return [yesterday.startOf('day'), yesterday.endOf('day')];
      case 'week':
        return [now.startOf('week'), now.endOf('week')];
      case 'month':
        return [now.startOf('month'), now.endOf('month')];
      case 'year':
        return [now.startOf('year'), now.endOf('year')];
      default:
        return [now.startOf('day'), now.endOf('day')];
    }
  }
}
